import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { finalize } from 'rxjs/operators';
import { WnPerms } from '../../../../../core/auth/worknest-admin-permissions';
import { apiErrorFallback, extractApiErrorMessage } from '../../../../../core/http/extract-api-error-message';
import { Translation } from '../../../../../core/i18n/translation.service';
import type { AiAgentTenantSettingsDto } from '../../../../../core/models/ai-agents/ai-agents.dto';
import { WorkNestModule } from '../../../../../core/models/tenant/worknest-module';
import { AiAgentsService } from '../../../../../core/services/ai-agents.service';
import { PermissionService } from '../../../../../core/services/permission.service';
import { TenantModulesService } from '../../../../../core/services/tenant-modules.service';
import { ToastFeedbackService } from '../../../../../core/services/toast-feedback.service';
import { ActionConfirmationService } from '../../../../../shared/services/action-confirmation.service';

/**
 * Organization settings › AI agents (spec §6.15, §11.8): the tenant's switches for AI agents, saved through
 * `PUT api/AiAgents/Settings` one property at a time (null = keep), never through the organization-settings save, which
 * HR keeps from touching this section. Shown only with the AI Agents add-on (module 16); editable with AiAgents.Manage.
 * SP3a adds the usage-statistics toggle; SP4b adds its toggles here (R5).
 */
@Component({
  selector: 'app-ai-agents-settings-card',
  standalone: true,
  imports: [TranslatePipe, ButtonModule],
  templateUrl: './ai-agents-settings-card.component.html',
  styleUrl: './ai-agents-settings-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiAgentsSettingsCardComponent {
  private readonly api = inject(AiAgentsService);
  private readonly modules = inject(TenantModulesService);
  private readonly permissions = inject(PermissionService);
  private readonly confirmation = inject(ActionConfirmationService);
  private readonly toast = inject(ToastFeedbackService);
  private readonly translation = inject(Translation);
  private requested = false;

  protected readonly visible = computed(() => this.modules.hasAny([WorkNestModule.AiAgents]));
  protected readonly canManage = computed(() => this.permissions.hasAny(WnPerms.aiAgents.manage));
  protected readonly settings = signal<AiAgentTenantSettingsDto | null>(null);
  protected readonly loadFailed = signal(false);
  protected readonly saving = signal(false);
  protected readonly error = signal<string | null>(null);

  constructor() {
    // Tenant modules may arrive after the page renders: read the settings the first time the add-on shows as enabled.
    effect(() => {
      if (this.visible() && !this.requested) {
        this.requested = true;
        untracked(() => this.load());
      }
    });
  }

  protected load(): void {
    this.loadFailed.set(false);
    this.api.getSettings().subscribe({
      next: (settings) => this.settings.set(settings),
      error: () => this.loadFailed.set(true),
    });
  }

  protected async toggleWrites(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const current = this.settings();
    const next = input.checked;
    if (!current || this.saving() || !this.canManage()) {
      input.checked = current?.writesEnabled ?? false;
      return;
    }
    if (!next) {
      const confirmed = await this.confirmation.confirm({
        headerKey: 'AI_AGENTS.ORG_SETTINGS.AI_WRITES_OFF_TITLE',
        messageKey: 'AI_AGENTS.ORG_SETTINGS.AI_WRITES_OFF_CONFIRM',
        acceptLabelKey: 'AI_AGENTS.ORG_SETTINGS.AI_WRITES_OFF_ACCEPT',
      });
      if (!confirmed || this.saving()) {
        input.checked = current.writesEnabled;
        return;
      }
    }
    this.saving.set(true);
    this.error.set(null);
    this.api
      .updateSettings({ writesEnabled: next })
      .pipe(finalize(() => this.saving.set(false)))
      .subscribe({
        next: (saved) => {
          this.settings.set(saved);
          // The box shows what HR saved, which can differ from the click.
          input.checked = saved.writesEnabled;
          // Only claim success when HR saved the value that was asked for.
          if (saved.writesEnabled === next) this.toast.successKey('AI_AGENTS.ORG_SETTINGS.SAVED', {});
        },
        error: (err: unknown) => {
          input.checked = current.writesEnabled;
          this.error.set(extractApiErrorMessage(err, apiErrorFallback(this.translation)));
        },
      });
  }

  /** SP4b: pay and bank data for AI agents. HR treats an unset value as on, so the box does too. */
  protected async toggleSensitive(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const current = this.settings();
    const before = current?.sensitiveDataEnabled ?? true;
    const next = input.checked;
    if (!current || this.saving() || !this.canManage()) {
      input.checked = before;
      return;
    }
    if (!next) {
      const confirmed = await this.confirmation.confirm({
        headerKey: 'AI_AGENTS.ORG_SETTINGS.AI_SENSITIVE_ENABLED',
        messageKey: 'AI_AGENTS.ORG_SETTINGS.AI_SENSITIVE_OFF_CONFIRM',
        acceptLabelKey: 'AI_AGENTS.ORG_SETTINGS.AI_WRITES_OFF_ACCEPT',
      });
      if (!confirmed || this.saving()) {
        input.checked = before;
        return;
      }
    }
    this.saving.set(true);
    this.error.set(null);
    this.api
      .updateSettings({ sensitiveDataEnabled: next })
      .pipe(finalize(() => this.saving.set(false)))
      .subscribe({
        next: (saved) => {
          this.settings.set(saved);
          const shown = saved.sensitiveDataEnabled ?? true;
          // The box shows what HR saved, which can differ from the click.
          input.checked = shown;
          if (shown === next) this.toast.successKey('AI_AGENTS.ORG_SETTINGS.SAVED', {});
        },
        error: (err: unknown) => {
          input.checked = before;
          this.error.set(extractApiErrorMessage(err, apiErrorFallback(this.translation)));
        },
      });
  }

  /** Collecting feature-usage statistics needs no confirmation: turning it off only stops new counting (the hint says what is kept). */
  protected toggleUsageTracking(event: Event): void {
    const input = event.target as HTMLInputElement;
    const current = this.settings();
    const next = input.checked;
    if (!current || this.saving() || !this.canManage()) {
      input.checked = current?.usageTrackingEnabled ?? false;
      return;
    }
    this.saving.set(true);
    this.error.set(null);
    this.api
      .updateSettings({ usageTrackingEnabled: next })
      .pipe(finalize(() => this.saving.set(false)))
      .subscribe({
        next: (saved) => {
          this.settings.set(saved);
          // The box shows what HR saved, which can differ from the click.
          input.checked = saved.usageTrackingEnabled;
          if (saved.usageTrackingEnabled === next) this.toast.successKey('AI_AGENTS.ORG_SETTINGS.SAVED', {});
        },
        error: (err: unknown) => {
          input.checked = current.usageTrackingEnabled;
          this.error.set(extractApiErrorMessage(err, apiErrorFallback(this.translation)));
        },
      });
  }
}
